import * as React from 'react';
import { ChevronRight, Moon, Search, Sun } from 'lucide-react';

import { cn } from '@/lib/cn';
import { Button, type ButtonProps } from '../button';
import { Kbd } from '../kbd';
import { Pressable } from '../pressable';
import { SHELL_GUTTER, isApplePlatform, isSearchShortcut, searchShortcutLabel } from './shell.logic';
import { useScrolled } from './use-scrolled';

export interface TopBarProps {
  /** Breadcrumbs, usually `<Breadcrumbs items={…} />`. */
  crumbs?: React.ReactNode;
  /** Centered search, usually `<SearchTrigger onOpen={…} />`. */
  search?: React.ReactNode;
  /** Right side: theme toggle, bell, help (`TopBarIconButton`s). */
  actions?: React.ReactNode;
  /**
   * Forces the glass state. Leave it out and the bar follows the page scroll
   * (`useScrolled`): no box at the top, glass once content slides under it.
   */
  scrolled?: boolean;
  className?: string;
}

/**
 * Header of the panel, to the right of the sidebar. Transparent at the top of
 * the page; past 8px of scroll it fades into glass (`--dur-2`). Its text lines up
 * with the page content (`PageHeader`) through the shell gutter.
 */
export function TopBar({ crumbs, search, actions, scrolled: scrolledProp, className }: TopBarProps) {
  const pageScrolled = useScrolled();
  const scrolled = scrolledProp ?? pageScrolled;
  return (
    <header
      data-slot="top-bar"
      data-scrolled={scrolled ? 'true' : 'false'}
      className={cn('sticky top-3 z-20 ml-4 mr-3 mt-3 flex h-14 shrink-0 items-center gap-2 rounded-card', className)}
      style={{
        paddingLeft: `calc(var(--shell-gutter, ${SHELL_GUTTER}) - 16px)`,
        paddingRight: `calc(var(--shell-gutter, ${SHELL_GUTTER}) - 21px)`,
      }}
    >
      <div className="relative flex min-w-0 items-center">{crumbs}</div>
      {search ? (
        <div className="absolute left-1/2 top-1/2 w-[min(380px,36%)] -translate-x-1/2 -translate-y-1/2">{search}</div>
      ) : null}
      <div className="relative ml-auto flex shrink-0 items-center gap-1.5">{actions}</div>
    </header>
  );
}

export interface BreadcrumbItem {
  label: React.ReactNode;
  /** Makes the crumb a link. The last crumb is the current page and never links. */
  href?: string;
}

/**
 * "ION SA › Tours". Parents are ink-3, the current page is ink. Pass
 * `renderLink` to route through the router: `(item, node) => <Link to={item.href}>{node}</Link>`.
 */
export function Breadcrumbs({
  items,
  renderLink,
  className,
}: {
  items: BreadcrumbItem[];
  renderLink?: (item: BreadcrumbItem, content: React.ReactNode) => React.ReactNode;
  className?: string;
}) {
  return (
    <nav data-slot="breadcrumbs" aria-label="Ubicación" className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 items-center gap-2 text-sm font-medium">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          const content = item.href && !last ? (renderLink ? renderLink(item, item.label) : <a href={item.href}>{item.label}</a>) : item.label;
          return (
            <li key={index} className="flex min-w-0 items-center gap-2">
              {index > 0 ? <ChevronRight className="size-3.5 shrink-0 text-ink-3" aria-hidden="true" /> : null}
              <span
                data-slot="breadcrumb"
                data-current={last ? '' : undefined}
                aria-current={last ? 'page' : undefined}
                className={cn('truncate', last ? 'font-semibold text-ink' : 'text-ink-3 [&_a]:rounded-control [&_a]:transition-colors [&_a:hover]:text-ink')}
              >
                {content}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export interface SearchTriggerProps {
  /** Opens the search (command palette). Also fired by Ctrl+K / ⌘K. */
  onOpen: () => void;
  placeholder?: string;
  /** Registers the global Ctrl+K / ⌘K shortcut the key hint advertises. On by default. */
  shortcut?: boolean;
  className?: string;
}

/**
 * Looks like a search field but is a button: it only opens the search. The key
 * hint follows the platform and is backed by a real global shortcut.
 */
export function SearchTrigger({ onOpen, placeholder = 'Buscar tours, panorámicas, ajustes…', shortcut = true, className }: SearchTriggerProps) {
  const [apple] = React.useState(() => typeof navigator !== 'undefined' && isApplePlatform(navigator.platform ?? ''));
  const openRef = React.useRef(onOpen);
  React.useEffect(() => {
    openRef.current = onOpen;
  });
  React.useEffect(() => {
    if (!shortcut) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !isSearchShortcut(event)) return;
      event.preventDefault();
      openRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shortcut]);

  return (
    <Pressable
      data-slot="search-trigger"
      onClick={onOpen}
      aria-label={placeholder}
      aria-keyshortcuts={shortcut ? (apple ? 'Meta+K' : 'Control+K') : undefined}
      className={cn(
        'flex h-[38px] w-full items-center gap-2 overflow-hidden whitespace-nowrap rounded-control border border-line-strong bg-surface pl-3.5 pr-2 text-left text-sm font-medium text-ink-3 shadow-e1',
        className,
      )}
    >
      <Search className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{placeholder}</span>
      {shortcut ? <Kbd>{searchShortcutLabel(apple)}</Kbd> : null}
    </Pressable>
  );
}

export interface TopBarIconButtonProps extends Omit<ButtonProps, 'variant' | 'size' | 'aria-label'> {
  /** Accessible name; also the tooltip. */
  label: string;
  /** Turquoise unread dot (notifications). */
  dot?: boolean;
}

/** 36px ghost icon button of the top bars. Rounded square (`--radius-button`), never a circle. */
export function TopBarIconButton({ label, dot, children, className, ...props }: TopBarIconButtonProps) {
  return (
    <Button variant="ghost" size="icon" aria-label={label} className={cn('relative [&_svg]:size-[18px]', className)} {...props}>
      {children}
      {dot ? <span data-slot="unread-dot" className="absolute right-[9px] top-[8px] size-2 rounded-pill bg-brand" aria-hidden="true" /> : null}
    </Button>
  );
}

/** Moon in light (go dark), sun in dark (go light). */
export function ThemeToggleButton({ dark, onToggle }: { dark: boolean; onToggle: (origin?: { x: number; y: number }) => void }) {
  return (
    <TopBarIconButton
      data-role="theme-toggle"
      label={dark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
      onClick={event => {
        // The theme change reveals as a circle from the center of this button.
        const box = event.currentTarget.getBoundingClientRect();
        onToggle({ x: box.left + box.width / 2, y: box.top + box.height / 2 });
      }}
    >
      {dark ? <Sun /> : <Moon />}
    </TopBarIconButton>
  );
}
